import { useSyncExternalStore } from "react";
import { useSettings } from "@helm/core";
import {
  describeRule,
  evaluatePrints,
  eventArrow,
  sanitizeRules,
  type AlertEvent,
  type AlertRule,
} from "@helm/ui";
import { marketData, priceFormatOf } from "../md/marketData";
import { pushToast } from "../components/Toasts";

/**
 * Local price alerts (owner 2026-09-27, mockup 02 "Alerts": Log / Rules / Watchers). Rules live in
 * this browser (`localStorage`) until a synced `alerts` document exists (docs/PLAN.md TODO) and
 * are evaluated here, client-side, against the live md prints of each rule's symbol (print by
 * print, integer ticks, `@helm/ui` alerts.ts). A trigger is a toast plus a short tone (when
 * Settings → Sounds is on) and a log line. Alerts never trade.
 *
 * The engine runs while the desktop shell is mounted (not only while the Alerts module is open),
 * keeping every armed rule's symbol watched on the window's shared md socket.
 */

const RULES_KEY = "helm:alerts:rules";
const LOG_KEY = "helm:alerts:log";
/** Log lines kept (newest first). */
export const ALERT_LOG_CAP = 200;

type State = { rules: readonly AlertRule[]; log: readonly AlertEvent[] };

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function save(key: string, v: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    // Private mode / quota: the list lasts for this page only.
  }
}

function sanitizeLog(v: unknown): AlertEvent[] {
  if (!Array.isArray(v)) return [];
  return (v as AlertEvent[])
    .filter((e) => typeof e === "object" && typeof e.id === "string" && Number.isFinite(e.priceTicks))
    .slice(0, ALERT_LOG_CAP);
}

let state: State = {
  rules: sanitizeRules(load<unknown>(RULES_KEY, [])),
  log: sanitizeLog(load<unknown>(LOG_KEY, [])),
};
const listeners = new Set<() => void>();

function set(next: Partial<State>): void {
  state = { ...state, ...next };
  if (next.rules) save(RULES_KEY, state.rules);
  if (next.log) save(LOG_KEY, state.log);
  for (const l of listeners) l();
  syncWatches();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useAlerts(): State {
  return useSyncExternalStore(subscribe, () => state);
}

/** Current rules and log (outside React). */
export function alertState(): State {
  return state;
}

let idSeq = 0;
const newId = (): string => `${Date.now().toString(36)}-${(++idSeq).toString(36)}`;

export const alerts = {
  add(rule: Omit<AlertRule, "id" | "createdMs" | "fired">): void {
    set({ rules: [...state.rules, { ...rule, id: newId(), createdMs: Date.now(), fired: 0 }] });
  },
  toggle(id: string): void {
    set({ rules: state.rules.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r)) });
  },
  remove(id: string): void {
    set({ rules: state.rules.filter((r) => r.id !== id) });
  },
  clearLog(): void {
    set({ log: [] });
  },
  /** Tests / preview: seed a log line without a market move. */
  logEvent(e: Omit<AlertEvent, "id">): void {
    set({ log: [{ ...e, id: newId() }, ...state.log].slice(0, ALERT_LOG_CAP) });
  },
  /** Chart alerts (drawings / indicators, chartAlerts.ts): log lines in trigger order + the tone. */
  logTriggered(events: readonly Omit<AlertEvent, "id">[]): void {
    if (events.length === 0) return;
    const lines = events.map((e) => ({ ...e, id: newId() })).reverse();
    set({ log: [...lines, ...state.log].slice(0, ALERT_LOG_CAP) });
    beep();
  },
};

// ------------------------------------------------------------------------------ evaluation

/** Symbols the engine keeps watched → last print id processed and last price. */
const watching = new Map<string, { lastId: number; last: number | null; off: () => void }>();
let running = 0;

function beep(): void {
  if (!useSettings.getState().settings.sounds) return;
  try {
    const Ctx = (window as { AudioContext?: typeof AudioContext }).AudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.3);
    o.onended = () => {
      void ctx.close();
    };
  } catch {
    // Autoplay policy before the first user gesture: the toast still shows.
  }
}

function onMarket(symbol: string): void {
  const w = watching.get(symbol);
  const m = marketData.get(symbol);
  if (!w || !m) return;
  const fresh = m.prints.filter((p) => p.id > w.lastId);
  if (fresh.length === 0) return;
  w.lastId = fresh[0]?.id ?? w.lastId;
  fresh.reverse(); // oldest first
  const r = evaluatePrints(state.rules, symbol, w.last, fresh, newId);
  w.last = r.last;
  if (r.events.length === 0) return;
  const fmt = priceFormatOf(m.info);
  set({ rules: r.rules, log: [...[...r.events].reverse(), ...state.log].slice(0, ALERT_LOG_CAP) });
  for (const e of r.events) {
    pushToast({
      tone: "info",
      title: `${eventArrow(e)} Alert · ${describeRule(e, fmt)}`,
      body: e.note || "Price alert (local rule). Alerts never place orders.",
    });
  }
  beep();
}

function syncWatches(): void {
  const want = new Set(running > 0 ? state.rules.filter((r) => r.enabled).map((r) => r.symbol) : []);
  for (const [sym, w] of watching) {
    if (want.has(sym)) continue;
    w.off();
    marketData.release(sym);
    watching.delete(sym);
  }
  for (const sym of want) {
    if (watching.has(sym)) continue;
    marketData.retain(sym);
    const cur = marketData.get(sym);
    const entry = {
      // Only prints after arming count: the latest known price is the baseline.
      lastId: cur?.prints[0]?.id ?? 0,
      last: cur?.lastTicks ?? null,
      off: marketData.listen(sym, () => {
        onMarket(sym);
      }),
    };
    watching.set(sym, entry);
  }
}

/** Start evaluating (desktop shell / preview). Ref-counted; returns the stop function. */
export function startAlertEngine(): () => void {
  running++;
  syncWatches();
  return () => {
    running--;
    syncWatches();
  };
}

/** Watchers tab: every symbol the engine holds, with its last price and armed rule count. */
export function watcherRows(): { symbol: string; last: number | null; armed: number }[] {
  return [...watching.entries()].map(([symbol, w]) => ({
    symbol,
    last: marketData.get(symbol)?.lastTicks ?? w.last,
    armed: state.rules.filter((r) => r.enabled && r.symbol === symbol).length,
  }));
}
